import { useEffect, useRef } from 'react';

/**
 * Requests that cross the app shell.
 *
 * The thing that ASKS for a dialog and the thing that OWNS it are often far
 * apart: the command palette, the sidebar's search button and the top bar's
 * buttons all want to open panels whose state lives in one component. These
 * are plain window events rather than a store because nothing needs to read
 * them back — they are requests, fire-and-forget — and because a component
 * that is not mounted simply does not hear one, which is the right answer.
 */
export type ShellEvent =
  | 'open-command-palette'
  | 'open-ai-import'
  | 'open-feedback';

const eventName = (event: ShellEvent) => `flow:${event}`;

export function emitShellEvent(event: ShellEvent): void {
  window.dispatchEvent(new Event(eventName(event)));
}

/** Run `handler` whenever `event` is requested, for as long as the caller is mounted. */
export function useShellEvent(event: ShellEvent, handler: () => void): void {
  // Latest handler without re-subscribing on every render.
  const latest = useRef(handler);
  latest.current = handler;

  useEffect(() => {
    const listener = () => latest.current();
    window.addEventListener(eventName(event), listener);
    return () => window.removeEventListener(eventName(event), listener);
  }, [event]);
}
